import type {MoodDTO} from '@/ts/types/dto/journals-dto';

/** Ink used for every facial feature. */
const INK = '#20282E';
/** Face colour on entry cards, where the tint is applied to the card instead. */
const FACE_YELLOW = '#fbcc44';

interface MoodVisual {
  /** Stored value, matches the backend `Mood` enum label. */
  key: MoodDTO;
  /** Face colour in the picker, and the entry card's background. */
  tint: string;
  /** SVG markup for the eyes, on a `0 0 38 38` viewBox. */
  eyes: string;
  /** SVG markup for the mouth, on a `0 0 38 38` viewBox. */
  mouth: string;
}

interface MoodsComposable {
  moods: MoodVisual[];
  getMood(key?: string | null): MoodVisual | undefined;
  getMoodTint(key?: string | null): string;
  /** `key` of the primary feeling family closest to this mood. */
  getMoodFeelingFamily(key?: string | null): string | undefined;
  getMoodForLegacyLevel(level?: string | null): MoodDTO | undefined;
}

/**
 * Read-time mapping from the legacy 5-point scale to the nearest new mood, so
 * entries written before the new vocabulary can still be drawn as cards
 * (IPD-2973 §6.2). Display only — the stored value is never rewritten, and the
 * old scale is never offered as input again.
 */
const LEGACY_LEVEL_TO_MOOD: Record<string, MoodDTO> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- API enum values
  very_low: 'sad',
  low: 'tired',
  okay: 'neutral',
  good: 'calm',
  great: 'joyful',
};

/**
 * Nearest primary-feeling family for each mood, so the feelings step can lead
 * with suggestions that relate to what the user picked.
 *
 * These are the primary feeling's `key`, the untranslated identifier the CMS
 * holds alongside the display name — matching on the name itself only worked
 * in English and fell through to the common set in every other language.
 * `neutral` deliberately maps to no family, as do the `anxious` and `rejected`
 * families, which no mood is close enough to.
 */
const MOOD_FEELING_FAMILY: Record<string, string> = {
  curious: 'surprised',
  joyful: 'happy',
  calm: 'happy',
  hopeful: 'happy',
  energised: 'happy',
  exhausted: 'sad',
  tired: 'sad',
  sad: 'sad',
  angry: 'angry',
};

const dot = (cx: number, cy: number, r = 1.6): string => `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${INK}"/>`;
const stroke = (d: string, width: number): string =>
  `<path d="${d}" stroke="${INK}" stroke-width="${width}" fill="none" stroke-linecap="round"/>`;

/**
 * The 10-mood vocabulary (IPD-2872), mirroring the backend `Mood` enum.
 *
 * Faces are drawn rather than shipped as image assets: a coloured circle plus
 * per-mood eye and mouth paths, so one definition scales from the 44px picker
 * to the 108px card face. Path geometry is ported verbatim from the design —
 * do not redraw it by eye.
 *
 * Declaration order is the picker order; do not sort.
 */
const MOODS: MoodVisual[] = [
  {
    key: 'curious',
    tint: '#cfc4e8',
    eyes: `${dot(13, 16.5)}${dot(25, 16.5)}${stroke('M22 12.4 Q25 10.8 28 12.4', 1.5)}`,
    mouth: `<ellipse cx="19" cy="24" rx="2.3" ry="2.7" fill="none" stroke="${INK}" stroke-width="1.5"/>`,
  },
  {
    key: 'joyful',
    tint: '#fde7a6',
    eyes: `${stroke('M11 16 Q13 14 15 16', 1.6)}${stroke('M23 16 Q25 14 27 16', 1.6)}`,
    mouth: stroke('M11 21 Q19 30 27 21', 1.8),
  },
  {
    key: 'calm',
    tint: '#a8d4c8',
    eyes: `${dot(13, 16)}${dot(25, 16)}`,
    mouth: stroke('M13 23 Q19 25.5 25 23', 1.6),
  },
  {
    key: 'hopeful',
    tint: '#bcd4ec',
    eyes: `${dot(13, 16)}${dot(25, 16)}`,
    mouth: stroke('M13 22.5 Q19 27 25 22.5', 1.7),
  },
  {
    key: 'energised',
    tint: '#f2ab6e',
    eyes: `${dot(13, 16, 2.3)}${dot(25, 16, 2.3)}`,
    mouth: stroke('M11 21 Q19 30 27 21', 1.8),
  },
  {
    key: 'neutral',
    tint: '#cfe0a8',
    eyes: `${dot(13, 16)}${dot(25, 16)}`,
    mouth: stroke('M13 23.5 L25 23.5', 1.7),
  },
  {
    key: 'exhausted',
    tint: '#bcc6b4',
    eyes: `${stroke('M10.5 16 L15.5 16', 1.6)}${stroke('M22.5 16 L27.5 16', 1.6)}`,
    mouth: stroke('M13 25 Q19 22 25 25', 1.6),
  },
  {
    key: 'tired',
    tint: '#b8c8d8',
    eyes: `${stroke('M11 15.5 Q13 18 15 15.5', 1.6)}${stroke('M23 15.5 Q25 18 27 15.5', 1.6)}`,
    mouth: stroke('M13 24.5 Q19 22.5 25 24.5', 1.6),
  },
  {
    key: 'sad',
    tint: '#aab9cb',
    eyes: `${dot(13, 16)}${dot(25, 16)}`,
    mouth: stroke('M13 25.5 Q19 19.5 25 25.5', 1.7),
  },
  {
    key: 'angry',
    tint: '#e39b86',
    eyes: `${dot(13, 17)}${dot(25, 17)}${stroke('M10 12.5 L16 15', 1.6)}${stroke('M28 12.5 L22 15', 1.6)}`,
    mouth: stroke('M13 25.5 Q19 20 25 25.5', 1.7),
  },
];

const MOOD_BY_KEY = new Map<string, MoodVisual>(MOODS.map((mood) => [mood.key, mood]));

/** Lookup helpers for the mood vocabulary and its picker/card colours. */
function useMoods(): MoodsComposable {
  function getMood(key?: string | null): MoodVisual | undefined {
    return MOOD_BY_KEY.get(key ?? '');
  }

  function getMoodTint(key?: string | null): string {
    return getMood(key)?.tint ?? FACE_YELLOW;
  }

  function getMoodFeelingFamily(key?: string | null): string | undefined {
    return MOOD_FEELING_FAMILY[key ?? ''];
  }

  function getMoodForLegacyLevel(level?: string | null): MoodDTO | undefined {
    return LEGACY_LEVEL_TO_MOOD[level ?? ''];
  }

  return {moods: MOODS, getMood, getMoodTint, getMoodFeelingFamily, getMoodForLegacyLevel};
}

export type {MoodVisual};
export {MOODS, FACE_YELLOW as MOOD_FACE_YELLOW, INK as MOOD_INK, useMoods};
